import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BuildingOffice2Icon,
  UserCircleIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import { useKnowledgeDocuments } from '../../../hooks/useKnowledgeDocuments';
import KnowledgeUploadDropzone from './KnowledgeUploadDropzone';
import KnowledgeDocumentList from './KnowledgeDocumentList';
import { resolveScope, scopeMeta, type KnowledgeScope } from './scopeMeta';

interface KnowledgePanelProps {
  orgId: string;
  stationId: string;
  deviceId?: string | null;
  includeStationShared?: boolean;
  compact?: boolean;
  /** Distinguish the manager's own scope from an employee device scope. */
  isManager?: boolean;
  /** Agent color, used to tint manager/device panels to their identity. */
  deviceColor?: string;
  /** Live device-agent count, shown in the station audience chip. */
  deviceCount?: number;
  /** Called after a successful upload so parent can refresh other panels. */
  onUploadSuccess?: () => void;
}

const SCOPE_ICON: Record<KnowledgeScope, typeof BuildingOffice2Icon> = {
  station: BuildingOffice2Icon,
  manager: UserCircleIcon,
  device: CpuChipIcon,
};

/** rgba from a #rrggbb hex at a given alpha — for low-chroma scope tints. */
function tint(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return `rgba(0, 82, 255, ${alpha})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

export default function KnowledgePanel({
  orgId,
  stationId,
  deviceId,
  includeStationShared = false,
  compact = false,
  isManager = false,
  deviceColor,
  deviceCount,
  onUploadSuccess,
}: KnowledgePanelProps) {
  const { t } = useTranslation();
  const { items, loading, uploading, error, upload, remove } = useKnowledgeDocuments({
    orgId,
    stationId,
    deviceId: deviceId ?? undefined,
    includeStationShared,
  });

  const handleUpload = async (file: File) => {
    await upload(file);
    onUploadSuccess?.();
  };

  const scope = resolveScope({ deviceId, isManager });
  const meta = scopeMeta(scope, t, { deviceColor, deviceCount });
  const Icon = SCOPE_ICON[scope];

  // Split this panel's own docs from station-shared docs inherited onto a device.
  const { ownItems, inheritedItems } = useMemo(() => {
    if (!includeStationShared || !deviceId) {
      return { ownItems: items, inheritedItems: [] };
    }
    return {
      ownItems: items.filter((d) => d.device_id),
      inheritedItems: items.filter((d) => !d.device_id),
    };
  }, [items, includeStationShared, deviceId]);

  return (
    <section
      className={`overflow-hidden rounded-lg border bg-app-surface shadow-sm transition-all ${compact ? '' : ''}`}
      style={{
        borderColor: tint(meta.color, 0.3),
        borderLeftWidth: '4px',
        borderLeftColor: meta.color,
      }}
    >
      <header className="flex flex-wrap items-center gap-2 border-b border-app-border/40 px-3 py-2 bg-app-surface">
        <Icon className="h-4 w-4 shrink-0" style={{ color: meta.color }} />
        <div className="min-w-0 flex-1 flex items-center gap-2">
          <h3 className="truncate text-sm font-semibold text-ink">{meta.title}</h3>
          <span
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
            style={{ background: tint(meta.color, 0.1), color: meta.color }}
          >
            {meta.audience}
          </span>
        </div>
        <span className="rounded-full bg-app-muted/20 px-2 py-0.5 text-[10px] font-bold tabular-nums text-app-text-dim">
          {ownItems.length}
        </span>
      </header>

      <div className="space-y-3 p-3">
        <KnowledgeUploadDropzone
          scope={scope}
          accentColor={meta.color}
          uploading={uploading}
          error={error}
          onUpload={handleUpload}
        />

        <KnowledgeDocumentList
          items={ownItems}
          loading={loading}
          onDelete={(id) => void remove(id)}
          emptyHint={meta.title}
        />

        {inheritedItems.length > 0 ? (
          <div className="space-y-1.5 border-t border-app-border/50 pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-app-text-dim">
              {t('knowledge.inheritedFromStation', {
                defaultValue: 'จากสถานี (ใช้ร่วมกับทุก agent)',
              })}
            </p>
            {/* Inherited docs are read-only here: delete them from the manager page. */}
            <KnowledgeDocumentList items={inheritedItems} inherited />
          </div>
        ) : null}
      </div>
    </section>
  );
}
